import { defineStore } from "pinia";
import { computed, ref } from "vue";

import { getApiErrorMessage } from "@/services/api-error";
import { UserSchema, type AuthUser, type LoginRequest } from "./auth.schema";
import {
  getCurrentUser,
  login as loginRequest,
  logout as logoutRequest,
} from "./auth.service";

const authUserStorageKey = "user";
const defaultAdminEmail = "admin@konek.com";

function normalizeStoredUser(user: AuthUser): AuthUser {
  if (user.email === defaultAdminEmail) {
    return {
      ...user,
      name: "Admin",
    };
  }

  return user;
}

function readStoredUser(): AuthUser | null {
  if (typeof localStorage === "undefined") return null;

  try {
    const storedUser = localStorage.getItem(authUserStorageKey);
    if (!storedUser) return null;

    const user = normalizeStoredUser(UserSchema.parse(JSON.parse(storedUser)));

    storeUser(user);

    return user;
  } catch {
    localStorage.removeItem(authUserStorageKey);

    return null;
  }
}

function storeUser(user: AuthUser | null) {
  if (typeof localStorage === "undefined") return;

  if (user) localStorage.setItem(authUserStorageKey, JSON.stringify(user));
  else localStorage.removeItem(authUserStorageKey);
}

const mergeKnownPermissions = (
  currentUser: AuthUser,
  knownUser: AuthUser | null,
): AuthUser => {
  if (
    currentUser.permissions.length > 0 ||
    !knownUser ||
    knownUser.permissions.length === 0 ||
    (currentUser.id !== knownUser.id && currentUser.email !== knownUser.email)
  )
    return currentUser;

  return {
    ...currentUser,
    permissions: knownUser.permissions,
  };
};

export const useAuthStore = defineStore("auth", () => {
  const user = ref<AuthUser | null>(readStoredUser());
  const isLoading = ref(false);
  const errorMessage = ref<string | null>(null);
  const hasCheckedAuth = ref(false);

  const isAuthenticated = computed(() => hasCheckedAuth.value && !!user.value);
  const permissions = computed(() => user.value?.permissions ?? []);

  async function login(payload: LoginRequest) {
    isLoading.value = true;
    errorMessage.value = null;

    try {
      const response = await loginRequest(payload);

      user.value = response.user;
      storeUser(response.user);
      hasCheckedAuth.value = true;

      return response;
    } catch (error) {
      errorMessage.value = getApiErrorMessage(error);
      throw error;
    } finally {
      isLoading.value = false;
    }
  }

  async function logout() {
    isLoading.value = true;
    errorMessage.value = null;

    try {
      await logoutRequest();
    } catch (error) {
      errorMessage.value = getApiErrorMessage(error);
    } finally {
      user.value = null;
      storeUser(null);
      hasCheckedAuth.value = true;
      isLoading.value = false;
    }
  }

  async function fetchCurrentUser(force = false) {
    if (hasCheckedAuth.value && !force) return user.value;

    isLoading.value = true;
    errorMessage.value = null;

    try {
      const currentUser = await getCurrentUser();
      const userWithPermissions = mergeKnownPermissions(
        currentUser,
        user.value,
      );

      user.value = userWithPermissions;
      storeUser(userWithPermissions);

      return userWithPermissions;
    } catch {
      user.value = null;
      storeUser(null);

      return null;
    } finally {
      hasCheckedAuth.value = true;
      isLoading.value = false;
    }
  }

  function clearError() {
    errorMessage.value = null;
  }

  function clearSession() {
    user.value = null;
    storeUser(null);
    hasCheckedAuth.value = true;
    isLoading.value = false;
  }

  function hasPermission(permission: string) {
    return permissions.value.includes(permission);
  }

  return {
    user,
    isLoading,
    errorMessage,
    hasCheckedAuth,
    isAuthenticated,
    permissions,
    login,
    logout,
    fetchCurrentUser,
    clearError,
    clearSession,
    hasPermission,
  };
});
